// src/pages/About.jsx
import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
  FaUtensils, FaTruck, FaLeaf, FaStar, 
  FaClock, FaShieldAlt, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaFacebook, FaInstagram,
  FaTwitter, FaYoutube, FaCheckCircle,
  FaUsers, FaAward, FaHeart, FaRocket,
  FaHandshake,
  FaCalendarCheck, FaShoppingCart, FaBoxOpen,
  FaCalendarAlt
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';

const About = () => {
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);

  // Company Features
  const features = [
    {
      icon: <FaUtensils className="text-3xl" />,
      title: 'Fresh & Healthy',
      description: 'We use only the freshest ingredients sourced from local farms to ensure every meal is nutritious and delicious.',
      color: '#10B981'
    },
    {
      icon: <FaTruck className="text-3xl" />,
      title: 'Fast Delivery',
      description: 'Our efficient delivery system ensures your food arrives hot and fresh within 30-45 minutes of ordering.',
      color: '#3B82F6'
    },
    {
      icon: <FaLeaf className="text-3xl" />,
      title: 'Eco-Friendly',
      description: 'We use biodegradable packaging and sustainable practices to minimize our environmental impact.',
      color: '#22C55E'
    },
    {
      icon: <FaClock className="text-3xl" />,
      title: '24/7 Service',
      description: 'Craving something at midnight? We deliver delicious tiffins round the clock, every single day.',
      color: '#F59E0B'
    },
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: 'Hygiene First',
      description: 'Our kitchens follow strict hygiene protocols and are regularly inspected for quality assurance.',
      color: '#EF4444'
    },
    {
      icon: <FaStar className="text-3xl" />,
      title: 'Authentic Taste',
      description: 'Every dish is prepared using traditional recipes passed down through generations.',
      color: '#8B5CF6'
    }
  ];

  // Services Offered
  const services = [
    {
      icon: <FaCalendarCheck className="text-3xl" />,
      title: 'Tiffin Service',
      description: 'Daily, weekly, or monthly tiffin subscriptions with customizable meal plans delivered to your doorstep.',
      features: ['Daily Tiffin', 'Monthly Subscription', 'Custom Date Selection', 'Flexible Plans'],
      color: '#FF6B35'
    },
    {
      icon: <FaShoppingCart className="text-3xl" />,
      title: 'Cart Service',
      description: 'Order individual meals from our extensive menu with flexible cart options for any occasion.',
      features: ['Individual Orders', 'Flexible Cart', 'Quick Checkout', 'Multiple Payment Options'],
      color: '#3B82F6'
    }
  ];

  // Booking Options
  const bookingOptions = [
    {
      icon: <FaCalendarAlt className="text-2xl" />,
      title: 'Monthly Subscription',
      description: 'Subscribe for a full month and enjoy daily meals with automatic delivery.',
      badge: 'Popular'
    },
    {
      icon: <FaCalendarCheck className="text-2xl" />,
      title: 'Custom Date Selection',
      description: 'Select specific dates for your tiffin deliveries based on your schedule.',
      badge: 'Flexible'
    },
    {
      icon: <FaShoppingCart className="text-2xl" />,
      title: 'Cart Wise Orders',
      description: 'Order any items from our menu and add to cart for quick checkout.',
      badge: 'Quick'
    },
    {
      icon: <FaBoxOpen className="text-2xl" />,
      title: 'Regular Tiffin',
      description: 'Standard tiffin service with consistent quality and timely delivery.',
      badge: 'Reliable'
    }
  ];

  // Company Stats
  const stats = [
    { number: '50+', label: 'Menu Items' },
    { number: '10K+', label: 'Happy Customers' },
    { number: '500+', label: 'Daily Orders' },
    { number: '4.8/5', label: 'Average Rating' },
    { number: '2026', label: 'Year of Launch' },
    { number: '98%', label: 'Satisfaction Rate' }
  ];

  // Core Values
  const values = [
    {
      title: 'Quality First',
      description: 'We never compromise on the quality of our ingredients and preparation.',
      icon: <FaAward />
    },
    {
      title: 'Customer Centric',
      description: 'Your satisfaction is our top priority. We go above and beyond to delight you.',
      icon: <FaHeart />
    },
    {
      title: 'Innovation',
      description: 'We constantly innovate to bring you new and exciting flavors.',
      icon: <FaRocket />
    },
    {
      title: 'Community',
      description: 'We believe in giving back to the community that supports us.',
      icon: <FaHandshake />
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* ===== HEADER ===== */}
        <motion.div 
          className="text-center py-8 sm:py-12 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ 
            backgroundColor: colors.primary + '15',
            color: colors.primary
          }}>
            <FaHeart className="text-xs" /> 
            <span className="text-sm font-medium">Est. 2026</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3" style={{ color: colors.text }}>
            About <span style={{ color: colors.primary }}>Foodies Tiffin</span>
          </h1>
          
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
            Delivering delicious, healthy, and affordable meals since 2026 through Tiffin Service and Cart Service.
          </p>
        </motion.div>

        {/* ===== STATS ===== */}
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 py-8 sm:py-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="p-3 sm:p-4 rounded-xl text-center"
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`
              }}
            >
              <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.primary }}>
                {stat.number}
              </div>
              <div className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* ===== OUR STORY ===== */}
        <motion.div 
          className="py-6 sm:py-8 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-3" style={{ color: colors.text }}>
            Our <span style={{ color: colors.primary }}>Story</span>
          </h2>
          
          <div className="space-y-3 text-sm sm:text-base" style={{ color: colors.textSecondary }}>
            <p>
              Foodies Tiffin was founded in <strong>2026</strong> with a simple vision: 
              to provide delicious, nutritious, and affordable meals through two convenient 
              services - <strong>Tiffin Service</strong> and <strong>Cart Service</strong>.
            </p>
            <p>
              Our <strong>Tiffin Service</strong> offers flexible subscription options including 
              monthly plans and custom date-wise selections, perfect for those who want 
              consistent, daily meals delivered to their doorstep.
            </p>
            <p>
              Our <strong>Cart Service</strong> allows customers to order individual meals 
              from our extensive menu, with easy cart management and quick checkout for 
              those who prefer flexibility in their dining choices.
            </p>
            <p>
              Today, we continue to uphold the same values of quality, taste, and customer 
              satisfaction that we started with. Every meal is prepared with love, using 
              the freshest ingredients and time-honored recipes.
            </p>
          </div>
        </motion.div>

        {/* ===== SERVICES ===== */}
        <motion.div 
          className="py-6 sm:py-8 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Our <span style={{ color: colors.primary }}>Services</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-4 sm:p-6 rounded-xl"
                style={{ 
                  backgroundColor: colors.card,
                  border: `2px solid ${service.color}`,
                }}
              >
                <div className="mb-3" style={{ color: service.color }}>
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-1" style={{ color: colors.text }}>
                  {service.title}
                </h3>
                <p className="text-sm mb-3" style={{ color: colors.textSecondary }}>
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {service.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full"
                      style={{ 
                        backgroundColor: service.color + '20',
                        color: service.color
                      }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ===== BOOKING OPTIONS ===== */}
        <motion.div 
          className="py-6 sm:py-8 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Booking <span style={{ color: colors.primary }}>Options</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {bookingOptions.map((option, index) => (
              <div
                key={index}
                className="p-4 rounded-xl text-center"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="text-2xl sm:text-3xl mb-2" style={{ color: colors.primary }}>
                  {option.icon}
                </div>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <h3 className="text-sm sm:text-base font-semibold" style={{ color: colors.text }}>
                    {option.title}
                  </h3>
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{ 
                    backgroundColor: colors.primary + '20',
                    color: colors.primary
                  }}>
                    {option.badge}
                  </span>
                </div>
                <p className="text-xs" style={{ color: colors.textSecondary }}>
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ===== FEATURES ===== */}
        <motion.div 
          className="py-6 sm:py-8 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Why <span style={{ color: colors.primary }}>Choose Us</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="mb-2" style={{ color: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="text-sm sm:text-base font-semibold mb-1" style={{ color: colors.text }}>
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ===== VALUES ===== */}
        <motion.div 
          className="py-6 sm:py-8 border-b"
          style={{ borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Core <span style={{ color: colors.primary }}>Values</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-4 rounded-xl text-center"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="text-2xl sm:text-3xl mb-2" style={{ color: colors.primary }}>
                  {value.icon}
                </div>
                <h3 className="text-sm sm:text-base font-semibold mb-1" style={{ color: colors.text }}>
                  {value.title}
                </h3>
                <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ===== CONTACT ===== */}
        <motion.div 
          className="py-6 sm:py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: colors.text }}>
            Get In <span style={{ color: colors.primary }}>Touch</span>
          </h2>
          
          <div 
            className="p-6 sm:p-8 rounded-xl"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                    <FaMapMarkerAlt style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: colors.text }}>Address</p>
                    <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>kalyani</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                    <FaPhone style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: colors.text }}>Phone</p>
                    <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>+91 98765 43210</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                    <FaEnvelope style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: colors.text }}>Email</p>
                    <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>support@foodiestiffin.com</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-sm sm:text-base mb-3" style={{ color: colors.text }}>
                  Follow Us
                </h4>
                <div className="flex gap-3">
                  <a 
                    href="#" 
                    className="p-2 sm:p-3 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                  >
                    <FaFacebook size={18} />
                  </a>
                  <a 
                    href="#" 
                    className="p-2 sm:p-3 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                  >
                    <FaInstagram size={18} />
                  </a>
                  <a 
                    href="#" 
                    className="p-2 sm:p-3 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                  >
                    <FaTwitter size={18} />
                  </a>
                  <a 
                    href="#" 
                    className="p-2 sm:p-3 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                  >
                    <FaYoutube size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
      </div>
    </div>
  );
};

export default About;